import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Database, Zap, Users, Building2, HardDrive, BarChart3, FileText, TrendingUp } from 'lucide-react';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import { getAllPerformanceMetrics } from '../../../services/performanceService';

const PerformanceHealthMonitor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // State for real metrics only
  const [platformStats, setPlatformStats] = useState(null);
  const [apiLatency, setApiLatency] = useState({ avg: 0, p50: 0, p95: 0, p99: 0 });
  const [largestCompanies, setLargestCompanies] = useState([]);
  const [heaviestContractors, setHeaviestContractors] = useState([]);

  useEffect(() => {
    loadPerformanceData();
  }, []);

  const loadPerformanceData = async () => {
    setLoading(true);
    try {
      const data = await getAllPerformanceMetrics();

      setPlatformStats(data.platformStats);
      setApiLatency(data.apiLatency);
      setLargestCompanies(data.largestCompanies);
      setHeaviestContractors(data.heaviestContractors);
    } catch (error) {
      console.error('Error loading performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLatencyColor = (value) => {
    if (value < 100) return 'text-green-600';
    if (value < 300) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <>
        <Header />
        <Sidebar />
        <div className="min-h-screen bg-dark-charcoal pt-[73px] pl-0 lg:pl-60">
          <div className="flex items-center justify-center min-h-[calc(100vh-73px)]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-orange mx-auto"></div>
              <p className="mt-4 text-white-shade-1">Loading platform statistics...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <Sidebar />
      <div className="min-h-screen bg-dark-charcoal pt-[73px] pl-0 lg:pl-60">
        {/* Page Header */}
        <div className="bg-surface-charcoal border-b border-charcoal-shade-2">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">
                  Platform Usage & Performance
                </h1>
                <p className="mt-1 text-sm text-white-shade-2">
                  Real-time platform-wide statistics for billing and management
                </p>
              </div>
              <button
                onClick={() => navigate('/super-admin/dashboard')}
                className="px-4 py-2 bg-surface-charcoal hover:bg-charcoal-shade-2 text-white-shade-1 border border-charcoal-shade-2 rounded-lg transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>

        <div className="px-8 py-8 space-y-8">
          {/* Platform-Wide Statistics */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Activity className="w-6 h-6 text-primary-orange" />
              Platform Statistics
            </h2>

            {/* Users Grid */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white-shade-1 mb-3">Users</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-white-shade-2">Total Users</h4>
                    <Users className="w-5 h-5 text-primary-orange" />
                  </div>
                  <p className="text-3xl font-bold text-white">{formatNumber(platformStats?.users?.total)}</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <h4 className="text-sm font-medium text-white-shade-2 mb-2">Contractors</h4>
                  <p className="text-3xl font-bold text-accent-green">{formatNumber(platformStats?.users?.contractors)}</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <h4 className="text-sm font-medium text-white-shade-2 mb-2">Corporate</h4>
                  <p className="text-3xl font-bold text-blue-500">{formatNumber(platformStats?.users?.corporate)}</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <h4 className="text-sm font-medium text-white-shade-2 mb-2">Super Admins</h4>
                  <p className="text-3xl font-bold text-purple-500">{formatNumber(platformStats?.users?.superAdmins)}</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <h4 className="text-sm font-medium text-white-shade-2 mb-2">Active (30d)</h4>
                  <p className="text-3xl font-bold text-yellow-500">{formatNumber(platformStats?.users?.active30d)}</p>
                </div>
              </div>
            </div>

            {/* Companies Grid */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white-shade-1 mb-3">Companies</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-white-shade-2">Total Companies</h4>
                    <Building2 className="w-5 h-5 text-primary-orange" />
                  </div>
                  <p className="text-3xl font-bold text-white">{formatNumber(platformStats?.companies?.total)}</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <h4 className="text-sm font-medium text-white-shade-2 mb-2">Active</h4>
                  <p className="text-3xl font-bold text-accent-green">{formatNumber(platformStats?.companies?.active)}</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <h4 className="text-sm font-medium text-white-shade-2 mb-2">Suspended</h4>
                  <p className="text-3xl font-bold text-red-500">{formatNumber(platformStats?.companies?.suspended)}</p>
                </div>
              </div>
            </div>

            {/* Jobs & Content Grid */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white-shade-1 mb-3">Jobs & Content</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-white-shade-2">Total Jobs</h4>
                    <BarChart3 className="w-5 h-5 text-primary-orange" />
                  </div>
                  <p className="text-3xl font-bold text-white">{formatNumber(platformStats?.jobs?.total)}</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <h4 className="text-sm font-medium text-white-shade-2 mb-2">Job Instances</h4>
                  <p className="text-3xl font-bold text-blue-500">{formatNumber(platformStats?.jobs?.instances)}</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <h4 className="text-sm font-medium text-white-shade-2 mb-2">Completed Jobs</h4>
                  <p className="text-3xl font-bold text-accent-green">{formatNumber(platformStats?.jobs?.completed)}</p>
                </div>
              </div>
            </div>

            {/* Storage & Database Grid */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white-shade-1 mb-3">Storage & Database</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-white-shade-2">Total Photos</h4>
                    <FileText className="w-5 h-5 text-primary-orange" />
                  </div>
                  <p className="text-3xl font-bold text-white">{formatNumber(platformStats?.storage?.totalPhotos)}</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-white-shade-2">Storage Used</h4>
                    <HardDrive className="w-5 h-5 text-accent-green" />
                  </div>
                  <p className="text-3xl font-bold text-accent-green">
                    {platformStats?.storage?.totalMB ? `${(platformStats.storage.totalMB / 1024).toFixed(2)} GB` : '0 GB'}
                  </p>
                  <p className="text-xs text-white-shade-2 mt-1">{platformStats?.storage?.totalMB?.toFixed(0)} MB</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <h4 className="text-sm font-medium text-white-shade-2 mb-2">Avg Photo Size</h4>
                  <p className="text-3xl font-bold text-yellow-500">{platformStats?.storage?.avgPhotoSizeMB?.toFixed(2)} MB</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-white-shade-2">Total Records</h4>
                    <Database className="w-5 h-5 text-blue-500" />
                  </div>
                  <p className="text-3xl font-bold text-blue-500">{formatNumber(platformStats?.database?.totalRecords)}</p>
                </div>

                <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                  <h4 className="text-sm font-medium text-white-shade-2 mb-2">Audit Logs</h4>
                  <p className="text-3xl font-bold text-purple-500">{formatNumber(platformStats?.database?.auditLogs)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* API Performance */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Zap className="w-6 h-6 text-primary-orange" />
              API Performance
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2 hover:shadow-md transition-shadow">
                <h4 className="text-sm font-medium text-white-shade-2 mb-2">Average Latency</h4>
                <p className={`text-3xl font-bold ${getLatencyColor(apiLatency?.avg)}`}>
                  {apiLatency?.avg}ms
                </p>
              </div>

              <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                <h4 className="text-sm font-medium text-white-shade-2 mb-2">P50 (Median)</h4>
                <p className={`text-3xl font-bold ${getLatencyColor(apiLatency?.p50)}`}>
                  {apiLatency?.p50}ms
                </p>
              </div>

              <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                <h4 className="text-sm font-medium text-white-shade-2 mb-2">P95</h4>
                <p className={`text-3xl font-bold ${getLatencyColor(apiLatency?.p95)}`}>
                  {apiLatency?.p95}ms
                </p>
              </div>

              <div className="bg-surface-charcoal rounded-lg shadow-sm p-6 border border-charcoal-shade-2">
                <h4 className="text-sm font-medium text-white-shade-2 mb-2">P99</h4>
                <p className={`text-3xl font-bold ${getLatencyColor(apiLatency?.p99)}`}>
                  {apiLatency?.p99}ms
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-white-shade-2 italic">
              * Measured from your browser to Supabase. Actual user latency may vary by location.
            </p>
          </div>

          {/* Top Companies by Data Volume */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-primary-orange" />
              Largest Companies by Data Volume
            </h2>
            <div className="bg-surface-charcoal rounded-lg shadow-sm border border-charcoal-shade-2 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-charcoal-shade-2">
                  <thead className="bg-charcoal-shade-2">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Tag
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Jobs
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Photos
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Storage
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Total Records
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-shade-2">
                    {largestCompanies?.length > 0 ? (
                      largestCompanies.map((company) => (
                        <tr key={company.id} className="hover:bg-charcoal-shade-2 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                            {company.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white-shade-2">
                            {company.tag}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white-shade-1 text-right">
                            {company.jobCount?.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white-shade-1 text-right">
                            {company.userCount?.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white-shade-1 text-right">
                            {company.photoCount?.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-green text-right font-medium">
                            {company.storageMB} MB
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-orange text-right font-medium">
                            {company.dataVolume}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 text-center text-sm text-white-shade-2">
                          No company data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Top Contractors by Storage Usage */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-primary-orange" />
              Heaviest Contractors by Storage Usage
            </h2>
            <div className="bg-surface-charcoal rounded-lg shadow-sm border border-charcoal-shade-2 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-charcoal-shade-2">
                  <thead className="bg-charcoal-shade-2">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Contractor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Uploads
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Storage Used
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white-shade-2 uppercase tracking-wider">
                        Avg File Size
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-shade-2">
                    {heaviestContractors?.length > 0 ? (
                      heaviestContractors.map((contractor) => (
                        <tr key={contractor.id} className="hover:bg-charcoal-shade-2 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                            {contractor.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white-shade-2">
                            {contractor.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white-shade-1 text-right">
                            {contractor.uploads?.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-green text-right font-medium">
                            {contractor.storageUsed}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-orange text-right font-medium">
                            {contractor.avgFileSize}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-white-shade-2">
                          No contractor data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="mt-3 text-sm text-white-shade-2 italic">

            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PerformanceHealthMonitor;
